/**
 * DATEVExportDialog — Lohnübergabe an DATEV LODAS.
 *
 * Erzeugt aus Stempelzeiten + Urlaub + Abwesenheiten eine CSV-Datei für
 * das Steuerberater-/Lohnbüro-Tool. Stammdaten (Mandant/Berater/Lohnarten)
 * werden aus den DATEV-Einstellungen vorbefüllt — können hier für diesen
 * einen Export überschrieben werden.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import { FileSpreadsheet, Loader2, Settings, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const LOHNART_FIELDS = [
  { key: 'regular',     label: 'Lohn / Gehalt (regulär)' },
  { key: 'overtime',    label: 'Mehrarbeit / Überstunden' },
  { key: 'sick',        label: 'Krankheit (K)' },
  { key: 'child_sick',  label: 'Kind krank (KK)' },
  { key: 'vacation',    label: 'Urlaub' },
  { key: 'holiday',     label: 'Feiertagsarbeit' },
  { key: 'sunday',      label: 'Sonntagsarbeit' },
  { key: 'parental',    label: 'Elternzeit (EZ)' },
  { key: 'ko_day',      label: 'Krank ohne Krankenschein (KO)' },
];

export const DATEVExportDialog = ({ open, onOpenChange, dateRange, scope }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showOverrides, setShowOverrides] = useState(false);
  const [overrideMandant, setOverrideMandant] = useState('');
  const [overrideBerater, setOverrideBerater] = useState('');
  const [overrideLohnarten, setOverrideLohnarten] = useState({});
  const [start, setStart] = useState(dateRange?.start || '');
  const [end, setEnd] = useState(dateRange?.end || '');

  useEffect(() => {
    if (!open) return;
    setStart(dateRange?.start || '');
    setEnd(dateRange?.end || '');
    setLoading(true);
    api.get('/datev/settings')
      .then((res) => {
        setSettings(res.data);
        setOverrideMandant('');
        setOverrideBerater('');
        setOverrideLohnarten({});
        setShowOverrides(false);
      })
      .catch(() => {
        toast.error('DATEV-Einstellungen konnten nicht geladen werden');
      })
      .finally(() => setLoading(false));
  }, [open, dateRange?.start, dateRange?.end]);

  const handleExport = async () => {
    if (!start || !end) {
      toast.error('Bitte Datumsbereich auswählen');
      return;
    }
    setExporting(true);
    try {
      const payload = {
        start_date: start,
        end_date: end,
        ...(scope?.employee_id && { employee_ids: [scope.employee_id] }),
        ...(scope?.location_id && { location_id: scope.location_id }),
        ...(scope?.branch_id && { branch_id: scope.branch_id }),
      };
      if (overrideMandant) payload.mandant_override = overrideMandant;
      if (overrideBerater) payload.berater_override = overrideBerater;
      if (Object.keys(overrideLohnarten).length > 0) {
        payload.lohnarten_override = Object.fromEntries(
          Object.entries(overrideLohnarten).filter(([, v]) => v && v.trim())
        );
      }
      const res = await api.post('/datev/export', payload, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : `DATEV_LODAS_${start}_bis_${end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('DATEV-Export erstellt');
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            DATEV-Lohnübergabe (LODAS)
          </DialogTitle>
          <DialogDescription>
            Exportiert Stempelzeiten, Urlaubs- und Krankheitstage im DATEV LODAS-Standard.
            Datei kann direkt von Steuerberater oder Lohnbüro importiert werden.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Lade Einstellungen…</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Von *</Label>
                <DatePickerDE value={start} onChange={setStart} placeholder="Startdatum" testId="datev-export-start" />
              </div>
              <div className="space-y-1.5">
                <Label>Bis *</Label>
                <DatePickerDE value={end} onChange={setEnd} placeholder="Enddatum" testId="datev-export-end" />
              </div>
            </div>

            <div className="rounded-md border border-border p-3 bg-secondary/20 space-y-1.5 text-sm">
              <div className="flex items-center gap-1.5 text-muted-foreground text-xs uppercase tracking-wide">
                <Info className="h-3.5 w-3.5" /> Aktive Stammdaten (aus DATEV-Einstellungen)
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-muted-foreground">Mandantennummer:</span>{' '}
                  <strong className="font-mono">{settings?.mandant}</strong>
                </div>
                <div>
                  <span className="text-muted-foreground">Beraternummer:</span>{' '}
                  <strong className="font-mono">{settings?.berater}</strong>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowOverrides((v) => !v)}
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              data-testid="datev-toggle-overrides"
            >
              <Settings className="h-3.5 w-3.5" />
              {showOverrides ? 'Einmaliges Überschreiben ausblenden' : 'Für diesen Export einmalig überschreiben…'}
            </button>

            {showOverrides && (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-3">
                <p className="text-xs text-amber-900 dark:text-amber-200">
                  Werte gelten nur für diesen einen Export. Permanente Änderungen in den DATEV-Einstellungen (Admin) vornehmen.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Mandant überschreiben</Label>
                    <Input value={overrideMandant} onChange={(e) => setOverrideMandant(e.target.value)} placeholder={settings?.mandant} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Berater überschreiben</Label>
                    <Input value={overrideBerater} onChange={(e) => setOverrideBerater(e.target.value)} placeholder={settings?.berater} />
                  </div>
                </div>
                <div className="space-y-1.5 pt-1">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Lohnarten überschreiben (optional)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {LOHNART_FIELDS.map((f) => (
                      <div key={f.key} className="flex items-center gap-2">
                        <span className="text-xs flex-1 truncate" title={f.label}>{f.label}</span>
                        <Input
                          className="w-24 font-mono"
                          placeholder={settings?.lohnarten?.[f.key] || '–'}
                          value={overrideLohnarten[f.key] || ''}
                          onChange={(e) => setOverrideLohnarten({ ...overrideLohnarten, [f.key]: e.target.value })}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" disabled={exporting}>
                Abbrechen
              </Button>
              <Button onClick={handleExport} disabled={exporting} className="rounded-full" data-testid="datev-export-submit">
                {exporting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird erstellt…</>
                ) : (
                  <><FileSpreadsheet className="mr-2 h-4 w-4" /> CSV erstellen</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
